import React from 'react';
import autoBind from 'react-autobind';
import Switch, { Case, Default } from 'react-switch-case';

import Config from "../../Config.jsx";
import Localization from "../../Localization.jsx"
import SubmissionDetailContent from "./SubmissionDetailContent.jsx"
import SmartToyIcon from '@mui/icons-material/SmartToy';

import {
  Icon,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  IconButton,
  Typography,
  Select,
  MenuItem,
  Box,
} from '@mui/material';

class SubmissionDetailAnswer extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();

    // state variables here
    this.state = {
      answerData: this.props.answerData,
      componentData: this.props.componentData,
      score: null,
      markingScheme: null,
      submission: this.props.submission,
      isEditingScore: false,
      tempScore: '',
      scoreSaving: false
    };
    autoBind(this);
  }

  componentDidMount() {
    if (!this.state.componentData) {
      return;
    }
    // Parse marking scheme for supported kinds (QUESTION, FILL_IN_THE_BLANK, TAKE_RECORD)
    const kind = parseInt(this.state.componentData.kind);
    if (
      kind === Config.HOTSPOT_CONTENT_QUESTION ||
      kind === Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK ||
      kind === Config.HOTSPOT_CONTENT_TAKE_RECORD
    ) {
      try {
        const payloadString = (this.state.componentData.payload || '').replace(/[\n\r]/g, '');
        const payload = payloadString ? JSON.parse(payloadString) : {};
        this.setState({
          markingScheme: {
            correctAnswer: payload.correctAnswer || '',
            answerToExclude: payload.answerToExclude || '',
            otherGuidelines: payload.otherGuidelines || ''
          }
        });
      } catch (error) {
        console.error("Failed to parse payload:", error);
        this.setState({
          markingScheme: {
            correctAnswer: '',
            answerToExclude: '',
            otherGuidelines: ''
          }
        });
      }
      
      // Set score for empty answers immediately
      const isEmptyAnswer = !this.state.answerData.answer || this.state.answerData.answer.trim() === '';
      if (isEmptyAnswer) {
        this.setState({ score: "0%" });
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Fetch AI assessment when marking scheme becomes valid
    if (this.hasValidMarkingScheme() && !prevState.markingScheme) {
      if (this.props.isSharedView) {
        // /api/ai/latestAssessment is bearer-gated, so a signed-out visitor on
        // a share link would only get a 401 and no score badge. The shared
        // answer row already carries the stored assessment.
        const stored = this.state.answerData.ai_assessment_result;
        if (stored) {
          this.setState({ score: this.extractScoreFromAIResult(stored) });
        }
        return;
      }
      this.props.root.refs.API.request(
        "get_latest_ai_assessment",
        { answer_id: this.state.answerData.id },
        this.getLatestAiAssessmentResultOK,
        this.getLatestAiAssessmentResultFailed
      );
    }
  }

  hasValidMarkingScheme = () => {
    if (!this.state.markingScheme) return false;
    
    const { correctAnswer, answerToExclude, otherGuidelines } = this.state.markingScheme;
    return !!(correctAnswer.trim() || answerToExclude.trim() || otherGuidelines.trim());
  }

  getLatestAiAssessmentResultOK = (res) => {
    if (!res.body || !res.body.result) {
      console.log("aiAssessmentResult: No assessment result available.");
    } else {
      const raw = res.body.result;
      const result = (typeof raw === 'string')
        ? raw
        : (raw.result || (raw.ai_processed_item && raw.ai_processed_item.response) || '');
      this.setState({ score: this.extractScoreFromAIResult(result) });
      console.log("aiAssessmentResult:", result);
    }
  }

  getLatestAiAssessmentResultFailed = (err) => {
    console.error("Failed to get latest AI assessment result:", err);
  }

  extractScoreFromAIResult = (aiResult) => {
    if (!aiResult) return null;
    const match = aiResult.match(/<(\d+)[%％]>/);  // Match both % and ％ symbols
    return match ? match[1] + '%' : null;
  }

  handleOpenMarkingSchemeDialog = () => {
    // Open the dialog through the content component
    console.log("handleOpenMarkingSchemeDialog called, ref exists:", !!this.refs.submissionContent);
    if (this.refs.submissionContent) {
      this.refs.submissionContent.handleOpenMarkingSchemeDialog();
    } else {
      console.error("submissionContent ref is not set!");
    }
  }

  compareAnswerClicked() {
    this.props.parent.props.parent.getAllAnswers(this.state.answerData, this.state.componentData);
  }

  startEditScore = () => {
    const rawAns = this.state.answerData && this.state.answerData.score;
    let ansNum = (rawAns !== undefined && rawAns !== null && rawAns !== '') ? parseFloat(rawAns) : null;
    // -1/null/NaN means "no score yet" — e.g. Drawing/Take Photo answers, which
    // are never AI-scored and so must be graded manually. Start editing from 0
    // rather than bailing out, otherwise those types could never be scored.
    if (ansNum === -1 || ansNum === null || isNaN(ansNum)) { ansNum = 0; }
    this.setState({ isEditingScore: true, tempScore: ansNum });
  }

  cancelEditScore = () => {
    this.setState({ isEditingScore: false });
  }

  saveScore = () => {
    if (!this.state.answerData || !this.state.answerData.id) { return; }
    const rawMax = this.state.componentData && this.state.componentData.score_max;
    const maxNum = (rawMax !== undefined && rawMax !== null && rawMax !== '') ? parseFloat(rawMax) : null;
    let newScore = Number(this.state.tempScore);
    if (isNaN(newScore) || newScore < 0) newScore = 0;
    if (maxNum !== null && !isNaN(maxNum)) {
      if (newScore > maxNum) newScore = maxNum;
    }

    this.setState({ scoreSaving: true });
    this.props.root.refs.API.request(
      "set_answer_score",
      { answer_id: this.state.answerData.id, new_score: newScore },
      (res) => {
        const updatedAnswer = { ...this.state.answerData, score: newScore };
        this.setState({ answerData: updatedAnswer, isEditingScore: false, scoreSaving: false });
      },
      (err) => {
        console.error('Failed to set answer score:', err);
        this.setState({ scoreSaving: false });
      }
    );
  }

  fillInTheBlank(kind) {
    if (kind != Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK) {
      return;
    }
    
    let payload = {};
    if (this.state.componentData && this.state.componentData.payload) {
      try{
        payload = JSON.parse(this.state.componentData.payload.replace(/[\n\r]/g, ''));
      }
      catch(err){
        // console.log('the following payload causes the error:');
        // for (let i = 0; i < this.state.componentData.payload.length; i++) {
        //   console.log(i.toString()+': '+this.state.componentData.payload[i].charCodeAt(0)+this.state.componentData.payload[i]);
        // }
        console.log(err);
      }
    }
    // let payload = JSON.parse(this.state.componentData.payload);
    // let payload = JSON.parse('{"text2":"","picture0":{"ok":1,"file_type":"jpg","message":"CC_qu qiao.jpg uploaded successfully.","file_id":163371,"path":"2017-3-29/1490800815___9wn3im2924cafjph.jpg","file_size":190819,"tmp_name":"/tmp/php8fenVX","move_file_result":true,"tn_path":"2017-3-29/s_1490800815___9wn3im2924cafjph.jpg","display_name":"CC_qu qiao"},"text1":"拱橋：人生道路有（1），安步能通彼岸。曲橋：人生道路有（2），徐行可達彼岸。"}');
    let textBefore = (payload.text1 != null? payload.text1 : "");
    let textAfter = (payload.text2 != null? payload.text2 : "");
    return (
        textBefore + " ( ... ) " + textAfter
    );
  }

  render() {
    let kind = parseInt(this.state.answerData.kind);
    const contentConfig = Config.GET_HOTSPOT_CONTENT_CONFIG(kind) || { icon: "help_outline", title: "Unknown", description: "" };
    let name = (this.state.componentData.name != null? this.state.componentData.name : "");
    let content = (this.state.componentData.content != null? this.state.componentData.content : "");

    if (name == "") {
      name = contentConfig.title;
    }

    return (
      <Box sx={{ 
        p: 2,
        boxSizing: 'border-box'
      }}>
        {/* Title Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Icon sx={{ mr: 2, color: 'action.active' }}>{contentConfig.icon}</Icon>
          <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold' }}>
            {name}
          </Typography>
        </Box>

        {/* Body Row: Content + Controls */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: 'flex-start',
          gap: 2
        }}>
          
          {/* Content Column */}
          <Box sx={{ flexGrow: 1, width: '100%', minWidth: 0 }}>
             <Box sx={{ mb: 2, color: 'text.secondary' }}>
                <Switch condition={kind}>
                  <Case value={Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK}>
                    <Typography variant="body2" sx={{ fontWeight: 'normal' }}>{this.fillInTheBlank(kind)}</Typography>
                  </Case>
                  <Default>
                    <Typography variant="body2" sx={{ fontWeight: 'normal' }}>{content}</Typography>
                  </Default>
                </Switch>
             </Box>
             
             <SubmissionDetailContent 
                ref={ref => this.refs.submissionContent = ref}
                root={this.props.root} 
                parent={this}
                answerData={this.state.answerData}
                componentData={this.state.componentData}
                isSharedView={this.props.isSharedView}
              />
          </Box>

          {/* Control Column */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'flex-end',
            flexShrink: 0,
            gap: 1
          }}>
            {this.props.isSharedView && (() => {
              const rawMax = this.state.componentData && this.state.componentData.score_max;
              const maxNum = (rawMax !== undefined && rawMax !== null && rawMax !== '') ? parseFloat(rawMax) : null;
              const canShow = (maxNum !== null && !isNaN(maxNum) && maxNum >= 0);
              if (!canShow) { return null; }
              const rawAns = this.state.answerData && this.state.answerData.score;
              let ansNum = (rawAns !== undefined && rawAns !== null && rawAns !== '') ? parseFloat(rawAns) : null;
              if (ansNum === -1 || ansNum === null || isNaN(ansNum)) { ansNum = 0; }
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Icon style={{ color: '#666', marginRight: '4px', fontSize: '20px' }}>emoji_events</Icon>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      ({ansNum}/{maxNum})
                    </Typography>
                  </div>
                  {this.state.score && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <SmartToyIcon style={{ color: '#ad1179', marginRight: '4px', fontSize: '20px' }} />
                      <Box
                        sx={{
                          backgroundColor: '#EEEEEE',
                          borderRadius: '10px',
                          padding: '2px 10px',
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{
                            color: parseInt(this.state.score) < 50 ? '#d32f2f' : '#2e7d32',
                            fontWeight: 'bold',
                          }}
                        >
                          {this.state.score}
                        </Typography>
                      </Box>
                    </div>
                  )}
                </div>
              );
            })()}
            
            {!this.props.isSharedView && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'flex-end', 
              gap: '6px'
            }}>
              {/* Compare answers control on top */}
              <Tooltip title={Localization.strings.COMPARE_ANSWERS}>
                <IconButton onClick={this.compareAnswerClicked} size="small"><Icon>group</Icon></IconButton>
              </Tooltip>
              {/* Score/Max row */}
              {(() => {
                const rawMax = this.state.componentData && this.state.componentData.score_max;
                const maxNum = (rawMax !== undefined && rawMax !== null && rawMax !== '') ? parseFloat(rawMax) : null;
                const canShow = (maxNum !== null && !isNaN(maxNum) && maxNum >= 0);
                if (!canShow) { return null; }
                const rawAns = this.state.answerData && this.state.answerData.score;
                const hasScore = (rawAns !== undefined && rawAns !== null && rawAns !== '' && parseFloat(rawAns) !== -1 && !isNaN(parseFloat(rawAns)));
                let ansNum = hasScore ? parseFloat(rawAns) : 0;
                return (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '32px', display: 'flex', justifyContent: 'center' }}>
                      <Icon style={{ color: '#666' }}>emoji_events</Icon>
                    </div>
                    {this.state.isEditingScore ? (
                      <div style={{ display: 'flex', alignItems: 'center', marginLeft: '4px' }}>
                        <Select
                          size="small"
                          value={this.state.tempScore}
                          onChange={(e) => this.setState({ tempScore: Number(e.target.value) })}
                          style={{ minWidth: '72px', marginRight: '4px' }}
                        >
                          {Array.from({ length: Math.max(0, Math.floor(maxNum) + 1) }).map((_, i) => (
                            <MenuItem key={i} value={i}>{i}</MenuItem>
                          ))}
                        </Select>
                        <Tooltip title={Localization.strings.SAVE || 'Save'}>
                          <span>
                            <IconButton size="small" onClick={this.saveScore} disabled={this.state.scoreSaving}>
                              <Icon>check</Icon>
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={Localization.strings.CANCEL || 'Cancel'}>
                          <IconButton size="small" onClick={this.cancelEditScore} disabled={this.state.scoreSaving}>
                            <Icon>close</Icon>
                          </IconButton>
                        </Tooltip>
                        <Typography variant="body2" style={{ marginLeft: '6px', color: '#666' }}>
                          / {maxNum}
                        </Typography>
                      </div>
                    ) : (
                      <Tooltip title={Localization.strings.EDIT_SCORE || 'Edit Score'}>
                        <Box
                          onClick={this.startEditScore}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: '4px',
                            backgroundColor: '#EEEEEE',
                            borderRadius: '10px',
                            padding: '4px 10px',
                            cursor: 'pointer'
                          }}
                        >
                          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                            ({ansNum}/{maxNum})
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </div>
                );
              })()}
              {/* AI icon + percentage row */}
              {(kind === Config.HOTSPOT_CONTENT_QUESTION || kind === Config.HOTSPOT_CONTENT_FILL_IN_THE_BLANK || kind === Config.HOTSPOT_CONTENT_TAKE_RECORD) && (
                (() => {
                  const canUseAIAssessment = this.props.root.getPrivilege('AI_AUTO_ASSESSMENT').value === '1';
                  const hasScheme = this.hasValidMarkingScheme();
                  const isDisabled = !canUseAIAssessment || !hasScheme;

                  let tooltipTitle;
                  if (!canUseAIAssessment) {
                    tooltipTitle = Localization.strings.UPGRADE_TO_PREMIUM || '請升級您的訂閱以使用此功能';
                  } else if (!hasScheme) {
                    tooltipTitle = Localization.strings.MARKING_SCHEME_MISSING;
                  } else {
                    tooltipTitle = Localization.strings.AI_ASSESSMENT;
                  }

                  return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ width: '32px', display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title={tooltipTitle}>
                          <span>
                            <IconButton
                              onClick={(e) => {
                                this.handleOpenMarkingSchemeDialog();
                              }}
                              style={{
                                color: '#ad1179',
                                opacity: isDisabled ? 0.5 : 1
                              }}
                              size="small"
                              disabled={isDisabled}
                            >
                              <SmartToyIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </div>
                      {this.hasValidMarkingScheme() && this.state.score && (
                        <Tooltip title={tooltipTitle}>
                          <Box 
                            onClick={!isDisabled ? this.handleOpenMarkingSchemeDialog : undefined}
                            sx={{
                              marginLeft: '4px',
                              backgroundColor: '#EEEEEE',
                              borderRadius: '10px',
                              padding: '4px 10px',
                              cursor: !isDisabled ? 'pointer' : 'default'
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              style={{ 
                                color: parseInt(this.state.score) < 50 ? '#d32f2f' : '#2e7d32',
                                fontWeight: 'bold',
                              }}
                            >
                              {this.state.score}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </div>
                  );
                })()
              )}
            </div>
            )}
          </Box>
        </Box>
      </Box>
    )
  }
}

export default SubmissionDetailAnswer;
